import {
  useGetAllCentersQuery,
  useAddCenterMutation,
  useGetCenterByIdQuery,
  useUpdateCenterMutation,
  useDeleteCenterMutation,
  useUploadCenterPhotoMutation,
  useUploadCenterDocsMutation,
  useGetCenterListQuery,
} from "../api/centerApi";
import { CenterResponseType } from "../types/centerResponse";
import { CenterPayloadType } from "../types/centerRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { PaginationProps } from "../../../../core/data/interface";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../../core/data/slice/authSlice";

export const useCenter = (params?: {
  size?: number;
  search?: string;
  page?: number;
  zone?: string;
  district?: string;
  type?: string;
  sort_by?: string;
  sort_order?: string;
} & { skip?: boolean }) => {
  const { data, isLoading, isError, error } = useGetAllCentersQuery(
    params || {},
    { skip: params?.skip }
  );

  const [
    addCenterMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddCenterMutation();

  const [
    updateCenterMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateCenterMutation();

  const [
    deleteCenterMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteCenterMutation();

  const [
    uploadPhotoMutation,
    { isLoading: isUploadingPhoto, error: photoUploadError },
  ] = useUploadCenterPhotoMutation();

  const [
    uploadDocsMutation,
    { isLoading: isUploadingDocs, error: docsUploadError },
  ] = useUploadCenterDocsMutation();

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;
  const centers: CenterResponseType[] = data?.data?.items || [];

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const addNewCenter = async (payload: CenterPayloadType) => {
    try {
      const response = await addCenterMutation(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: addErrorMessage || "Failed to add center",
      };
    }
  };

  const useGetCenterById = (id: string) => {
    return useGetCenterByIdQuery(
      { id },
      {
        skip: !id,
      }
    );
  };

  const updateCenter = async (id: string, payload: CenterPayloadType) => {
    try {
      const response = await updateCenterMutation({ id, payload }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: updateErrorMessage || "Failed to update center",
      };
    }
  };

  const deleteCenter = async (id: string) => {
    try {
      const response = await deleteCenterMutation({ id }).unwrap();
      return { success: true, data: response.data };
    } catch (error: any) {
      if (error.status === 409) {
        return { 
          success: false, 
          error: "This center has associated records that prevent deletion. Would you like to deactivate the center instead?",
          status: 409 
        };
      }
      return {
        success: false,
        error: getApiErrorMessage(error) || "Could not delete the center",
      };
    }
  };

  const uploadCenterPhoto = async (id: string, image: File) => {
    try {
      const response = await uploadPhotoMutation({ id, image }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to upload photo",
      };
    }
  };

  const uploadCenterDocs = async (
    id: string,
    docType: "icard" | "address_proof",
    file: File
  ) => {
    try {
      const response = await uploadDocsMutation({ id, docType, file }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to upload document",
      };
    }
  };

  const getCenterState = () => ({
    centers,
    isLoading,
    error: errorMessage,
  });

  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";

  const useCenterList = (options?: { skip?: boolean }) => {
    return useGetCenterListQuery(undefined, { skip: options?.skip });
  };

  return {
    getCenterState,
    useCenterList,
    useGetCenterById,
    addNewCenter,
    updateCenter,
    deleteCenter,
    uploadCenterPhoto,
    uploadCenterDocs,
    isAdding,
    isUpdating,
    isDeleting,
    isUploadingPhoto,
    isUploadingDocs,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
    paginationData,
  };
};
